/**
 * Offline outbox: queued actions are replayed to the server in order.
 *
 * - Every item has a stable `id` used as the Idempotency-Key, so a retry after
 *   a dropped response never creates a duplicate.
 * - Network failure or 5xx / 408 / 429: stop, keep item pending, try later.
 * - Other 4xx (rejected by the server): item becomes `needs_attention` and the
 *   queue continues. Nothing is dropped silently.
 */
export function isRetryable(error) {
  const status = error?.response?.status;
  if (!status) return true;
  return status >= 500 || status === 408 || status === 429;
}

export function errorText(error) {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return error?.message || "Rejected by server";
}

const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export function createOutbox(store, { send, deviceCode = "DEV", now = () => new Date().toISOString() }) {
  let flushing = null;

  let seqChain = Promise.resolve();
  const nextSeq = () => {
    const step = seqChain.then(async () => {
      const meta = (await store.get("meta", "seq")) || 0;
      await store.put("meta", "seq", meta + 1);
      return meta + 1;
    });
    seqChain = step.catch(() => {});
    return step;
  };

  const list = async () =>
    (await store.getAll("outbox")).sort((a, b) => a.seq - b.seq);

  return {
    list,

    async enqueue(kind, payload) {
      const seq = await nextSeq();
      const item = {
        id: newId(), seq, kind, payload,
        provisionalNumber: `${deviceCode}-${String(seq).padStart(5, "0")}`,
        status: "pending", attempts: 0, createdAt: now(), error: null,
      };
      await store.put("outbox", item.id, item);
      return item;
    },

    async counts() {
      const items = await list();
      return {
        pending: items.filter((i) => i.status === "pending").length,
        attention: items.filter((i) => i.status === "needs_attention").length,
      };
    },

    flush() {
      if (flushing) return flushing;
      flushing = (async () => {
        const result = { sent: 0, failed: 0, stopped: false };
        for (const item of await list()) {
          if (item.status !== "pending") continue;
          try {
            await send(item);
            await store.remove("outbox", item.id);
            result.sent += 1;
          } catch (e) {
            const next = { ...item, attempts: item.attempts + 1, error: errorText(e) };
            if (isRetryable(e)) {
              await store.put("outbox", item.id, next);
              result.stopped = true;
              break;
            }
            await store.put("outbox", item.id, { ...next, status: "needs_attention" });
            result.failed += 1;
          }
        }
        return result;
      })().finally(() => { flushing = null; });
      return flushing;
    },

    async retry(id) {
      const item = await store.get("outbox", id);
      if (item) await store.put("outbox", id, { ...item, status: "pending", error: null });
    },

    async discard(id) {
      await store.remove("outbox", id);
    },
  };
}
